export type DashboardAccountRole = 'organizer' | 'bar';

const ROLE_KEYS: Record<DashboardAccountRole, { id: string; email: string; name: string; userId: string }> = {
  organizer: {
    id: 'activeOrganizerId',
    email: 'activeOrganizerEmail',
    name: 'activeOrganizerName',
    userId: 'activeOrganizerUserId',
  },
  bar: {
    id: 'activeBarId',
    email: 'activeBarEmail',
    name: 'activeBarName',
    userId: 'activeBarUserId',
  },
};

const GENERIC_KEYS = {
  id: 'activeAccountId',
  email: 'activeAccountEmail',
  name: 'activeAccountName',
  type: 'activeAccountType',
  userId: 'activeAccountUserId',
};

const isDashboardRole = (value: string | null | undefined): value is DashboardAccountRole => value === 'organizer' || value === 'bar';

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage && !!window.sessionStorage;

const readCookie = (name: string) => {
  if (!isBrowser()) return null;
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)')) || null;
    return match && match[1] ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
};

const getPathRole = (): DashboardAccountRole | null => {
  if (!isBrowser()) return null;
  try {
    const match = window.location.pathname.match(/\/dashboard\/(organizer|bar)(?:\/|$)/);
    return match && isDashboardRole(match[1]) ? match[1] : null;
  } catch (e) {
    return null;
  }
};

export const getDashboardAccountRole = (): DashboardAccountRole | null => {
  if (!isBrowser()) return null;
  try {
    const stored = sessionStorage.getItem(GENERIC_KEYS.type) || localStorage.getItem(GENERIC_KEYS.type) || null;
    return isDashboardRole(stored) ? stored : getPathRole();
  } catch (e) {
    return getPathRole();
  }
};

const getRoleKeys = (role?: DashboardAccountRole | null) => {
  const resolvedRole = role || getPathRole() || getDashboardAccountRole();
  return resolvedRole ? ROLE_KEYS[resolvedRole] : null;
};

const readStorageValue = (key: string) => {
  if (!isBrowser()) return null;
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key) || null;
  } catch (e) {
    return null;
  }
};

const readScopedValue = (field: 'id' | 'email' | 'name' | 'userId', role?: DashboardAccountRole | null) => {
  const roleKeys = getRoleKeys(role);
  if (roleKeys) {
    const roleValue = readStorageValue(roleKeys[field]);
    if (roleValue) return roleValue;
  }

  const currentRole = getDashboardAccountRole();
  if (!role || currentRole === role) {
    const genericValue = readStorageValue(GENERIC_KEYS[field === 'userId' ? 'userId' : field]);
    if (genericValue) return genericValue;
  }

  if (!role) {
    const cookieValue = field === 'id' ? readCookie('accountId') : field === 'email' ? readCookie('accountEmail') : field === 'name' ? readCookie('accountName') : null;
    if (cookieValue) return cookieValue;
  }

  return null;
};

export const getDashboardAccountId = (role?: DashboardAccountRole | null) => readScopedValue('id', role);
export const getDashboardAccountEmail = (role?: DashboardAccountRole | null) => readScopedValue('email', role);
export const getDashboardAccountName = (role?: DashboardAccountRole | null) => readScopedValue('name', role);
export const getDashboardAccountUserId = (role?: DashboardAccountRole | null) => readScopedValue('userId', role);

const writeValue = (storage: Storage, key: string, value: string | null | undefined) => {
  try {
    if (value === null || value === undefined || value === '') storage.removeItem(key);
    else storage.setItem(key, String(value));
  } catch (e) {
    /* ignore */
  }
};

export const setDashboardAccountState = (role: DashboardAccountRole | null, data: { id?: string | null; email?: string | null; name?: string | null; userId?: string | null }) => {
  if (!isBrowser()) return;
  const resolvedRole = role || null;
  const roleKeys = resolvedRole ? ROLE_KEYS[resolvedRole] : null;

  writeValue(sessionStorage, GENERIC_KEYS.id, data.id || null);
  writeValue(sessionStorage, GENERIC_KEYS.email, data.email || null);
  writeValue(sessionStorage, GENERIC_KEYS.name, data.name || null);
  writeValue(sessionStorage, GENERIC_KEYS.userId, data.userId || null);
  writeValue(sessionStorage, GENERIC_KEYS.type, resolvedRole || null);

  writeValue(localStorage, GENERIC_KEYS.id, data.id || null);
  writeValue(localStorage, GENERIC_KEYS.email, data.email || null);
  writeValue(localStorage, GENERIC_KEYS.name, data.name || null);
  writeValue(localStorage, GENERIC_KEYS.userId, data.userId || null);
  writeValue(localStorage, GENERIC_KEYS.type, resolvedRole || null);

  if (roleKeys) {
    writeValue(sessionStorage, roleKeys.id, data.id || null);
    writeValue(sessionStorage, roleKeys.email, data.email || null);
    writeValue(sessionStorage, roleKeys.name, data.name || null);
    writeValue(sessionStorage, roleKeys.userId, data.userId || null);

    writeValue(localStorage, roleKeys.id, data.id || null);
    writeValue(localStorage, roleKeys.email, data.email || null);
    writeValue(localStorage, roleKeys.name, data.name || null);
    writeValue(localStorage, roleKeys.userId, data.userId || null);
  }
};

export const clearDashboardAccountState = () => {
  if (!isBrowser()) return;
  const keys = [
    GENERIC_KEYS.id,
    GENERIC_KEYS.email,
    GENERIC_KEYS.name,
    GENERIC_KEYS.type,
    GENERIC_KEYS.userId,
    ROLE_KEYS.organizer.id,
    ROLE_KEYS.organizer.email,
    ROLE_KEYS.organizer.name,
    ROLE_KEYS.organizer.userId,
    ROLE_KEYS.bar.id,
    ROLE_KEYS.bar.email,
    ROLE_KEYS.bar.name,
    ROLE_KEYS.bar.userId,
  ];
  try {
    keys.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  } catch (e) {
    /* ignore */
  }
};

/**
 * Guard against the shared-cookie multi-tab problem: the browser has a single
 * cookie jar, so logging in as a different account in another tab silently
 * overwrites the session this tab relies on. Before a sensitive action (e.g.
 * buying a ticket) we compare this tab's identity (per-tab sessionStorage)
 * against the identity the server actually sees (the cookie, via whoami).
 *
 * Returns `match: false` only when both are known and differ — i.e. a real
 * "another tab is logged in as someone else" situation.
 */
export async function verifyActiveAccountMatchesServer(): Promise<{ match: boolean; tabEmail: string | null; serverEmail: string | null }> {
  if (!isBrowser()) return { match: true, tabEmail: null, serverEmail: null };
  let tabEmail: string | null = null;
  try {
    tabEmail = ((sessionStorage.getItem(GENERIC_KEYS.email) || sessionStorage.getItem('guestEmail') || '').trim().toLowerCase()) || null;
  } catch (e) { tabEmail = null; }
  // No per-tab identity stored → we cannot detect a mismatch; allow.
  if (!tabEmail) return { match: true, tabEmail: null, serverEmail: null };
  let serverEmail: string | null = null;
  try {
    const resp = await fetch('/api/auth/whoami', { method: 'GET', credentials: 'include' });
    const who = await resp.json().catch(() => null);
    if (who && who.loggedIn && who.user && who.user.email) serverEmail = String(who.user.email).trim().toLowerCase();
  } catch (e) { serverEmail = null; }
  // Server reports a different logged-in account than this tab → mismatch.
  if (serverEmail && serverEmail !== tabEmail) return { match: false, tabEmail, serverEmail };
  return { match: true, tabEmail, serverEmail };
}